import { LISTING_LIMITS } from '../constants.js';
import { deriveCombinationsList, resolveEffectiveCombinationPrice, syncVariationImages, } from '../domain/listing.js';
import { isValidPriceCents, stripHtml, ValidationField, } from './shared.js';
export const validateTitle = (title) => {
    const trimmed = title.trim();
    if (!trimmed)
        return {
            field: ValidationField.Title,
            message: 'Title is required.',
        };
    if (trimmed.length > LISTING_LIMITS.maxTitleLength) {
        return {
            field: ValidationField.Title,
            message: `Title must be ${LISTING_LIMITS.maxTitleLength} characters or fewer.`,
        };
    }
    return null;
};
export const validateSubtitle = (subtitle) => {
    const trimmed = (subtitle ?? '').trim();
    if (trimmed.length > LISTING_LIMITS.maxSubtitleLength) {
        return {
            field: ValidationField.Subtitle,
            message: `Subtitle must be ${LISTING_LIMITS.maxSubtitleLength} characters or fewer.`,
        };
    }
    return null;
};
export const validateInventory = (trackInventory, inventory) => {
    if (!trackInventory)
        return null;
    if (!isValidInventoryValue(inventory)) {
        return {
            field: ValidationField.Inventory,
            message: `Must be a whole number between 0 and ${LISTING_LIMITS.maxInventory.toLocaleString()}.`,
        };
    }
    return null;
};
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const validateImageUuids = (imageUuids) => {
    if (imageUuids.length > LISTING_LIMITS.maxImages) {
        return {
            field: ValidationField.Images,
            message: `A listing can have at most ${LISTING_LIMITS.maxImages} images.`,
        };
    }
    if (imageUuids.some((id) => !UUID_RE.test(id))) {
        return {
            field: ValidationField.Images,
            message: 'One or more images are invalid.',
        };
    }
    return null;
};
export const validateOptionDeletion = (optionId, defaultOption) => optionId === defaultOption
    ? {
        field: ValidationField.VariationDefaultOption,
        message: 'The default option cannot be deleted.',
    }
    : null;
export const validateVariationEntry = (variation, existingNames) => {
    const errors = [];
    const trimmedName = variation.name.trim();
    if (!trimmedName) {
        errors.push({
            field: ValidationField.VariationName,
            message: 'Name is required.',
        });
    }
    else if (trimmedName.length > LISTING_LIMITS.maxNameLength) {
        errors.push({
            field: ValidationField.VariationName,
            message: `Name must be ${LISTING_LIMITS.maxNameLength} characters or fewer.`,
        });
    }
    else if (existingNames.some((n) => n.toLowerCase() === trimmedName.toLowerCase())) {
        errors.push({
            field: ValidationField.VariationName,
            message: 'A variation with this name already exists.',
        });
    }
    const trimmedOptions = variation.options.map((o) => o.name.trim());
    const lowered = trimmedOptions.map((n) => n.toLowerCase());
    if (variation.options.length < 2) {
        errors.push({
            field: ValidationField.VariationOptions,
            message: 'At least two options are required.',
        });
    }
    else if (variation.options.length >
        LISTING_LIMITS.maxOptionsPerVariation) {
        errors.push({
            field: ValidationField.VariationOptions,
            message: `A variation can have at most ${LISTING_LIMITS.maxOptionsPerVariation} options.`,
        });
    }
    else if (trimmedOptions.some((n) => !n)) {
        errors.push({
            field: ValidationField.VariationOptions,
            message: 'All options must have a name.',
        });
    }
    else if (trimmedOptions.some((n) => n.length > LISTING_LIMITS.maxNameLength)) {
        errors.push({
            field: ValidationField.VariationOptions,
            message: `Option names must be ${LISTING_LIMITS.maxNameLength} characters or fewer.`,
        });
    }
    else if (new Set(lowered).size !== lowered.length) {
        errors.push({
            field: ValidationField.VariationOptions,
            message: 'Option names must be unique.',
        });
    }
    if (variation.options.some((o) => o.priceCents != null &&
        !isValidPriceCents(o.priceCents, LISTING_LIMITS.maxPriceCents))) {
        errors.push({
            field: ValidationField.VariationOptions,
            message: 'Option prices must be valid.',
        });
    }
    if (!variation.defaultOption) {
        errors.push({
            field: ValidationField.VariationDefaultOption,
            message: 'A default option is required.',
        });
    }
    else if (!variation.options.some((o) => o.id === variation.defaultOption)) {
        errors.push({
            field: ValidationField.VariationDefaultOption,
            message: 'The default option must be one of the options.',
        });
    }
    return errors;
};
// Mirrors the duplicate/length/required predicates in validateVariationEntry,
// but returns the offending indices instead of messages so the option-entry
// UI can highlight exactly the rows at fault.
export const findInvalidVariationOptionIndices = (options) => {
    const trimmedOptions = options.map((o) => o.name.trim());
    const lowered = trimmedOptions.map((n) => n.toLowerCase());
    const invalid = new Set();
    trimmedOptions.forEach((name, i) => {
        if (!name || name.length > LISTING_LIMITS.maxNameLength)
            invalid.add(i);
    });
    lowered.forEach((name, i) => {
        if (lowered.indexOf(name) !== lowered.lastIndexOf(name))
            invalid.add(i);
    });
    return [...invalid];
};
export const validateDescrSectionEntry = (section, existingTitles) => {
    const errors = [];
    const trimmedTitle = section.title.trim();
    if (!trimmedTitle) {
        errors.push({
            field: ValidationField.DescrSectionTitle,
            message: 'Title is required.',
        });
    }
    else if (trimmedTitle.length > LISTING_LIMITS.maxNameLength) {
        errors.push({
            field: ValidationField.DescrSectionTitle,
            message: `Title must be ${LISTING_LIMITS.maxNameLength} characters or fewer.`,
        });
    }
    else if (existingTitles.some((t) => t.trim() === trimmedTitle)) {
        errors.push({
            field: ValidationField.DescrSectionTitle,
            message: 'A section with this title already exists.',
        });
    }
    const strippedBody = stripHtml(section.richText);
    if (!strippedBody) {
        errors.push({
            field: ValidationField.DescrSectionBody,
            message: 'Body is required.',
        });
    }
    else if (strippedBody.length > LISTING_LIMITS.maxDescrSectionBodyChars) {
        errors.push({
            field: ValidationField.DescrSectionBody,
            message: `Body must be ${LISTING_LIMITS.maxDescrSectionBodyChars.toLocaleString()} characters or fewer.`,
        });
    }
    return errors;
};
const isValidInventoryValue = (inventory) => inventory != null &&
    Number.isInteger(inventory) &&
    inventory >= 0 &&
    inventory <= LISTING_LIMITS.maxInventory;
export const findInvalidCombinations = (variations, combinations, basePriceCents, trackInventory) => {
    const allCombinations = deriveCombinationsList(variations);
    const pricesVary = Object.values(variations).some((v) => v.pricesVary);
    let hasActive = allCombinations.length === 0;
    const missingPriceKeys = [];
    const missingInventoryKeys = [];
    for (const { key, optionMap } of allCombinations) {
        const entry = combinations[key];
        if (entry?.disabled)
            continue;
        hasActive = true;
        if (pricesVary) {
            const effectivePrice = resolveEffectiveCombinationPrice(optionMap, combinations, variations, null);
            if (!(effectivePrice && effectivePrice > 0))
                missingPriceKeys.push(key);
        }
        if (trackInventory &&
            !isValidInventoryValue(entry?.inventory))
            missingInventoryKeys.push(key);
    }
    return { hasActive, missingPriceKeys, missingInventoryKeys };
};
export const validateVariationImages = (variations, variationImages) => {
    const expectedKeys = Object.keys(syncVariationImages(null, variations) ?? {});
    const actualImages = variationImages ?? {};
    const hasExpectedStructure = Object.keys(actualImages).length === expectedKeys.length &&
        expectedKeys.every((key) => key in actualImages);
    if (hasExpectedStructure)
        return null;
    return {
        field: ValidationField.VariationImages,
        message: expectedKeys.length === 0
            ? 'Variation images are only allowed when two or more variations vary images.'
            : 'Variation images must be defined for every combination of the variations that vary images.',
    };
};
export const validateListingFields = (input, options) => {
    const errors = [];
    const titleError = validateTitle(input.title);
    if (titleError)
        errors.push(titleError);
    const subtitleError = validateSubtitle(input.subtitle);
    if (subtitleError)
        errors.push(subtitleError);
    if (!input.categoryId) {
        errors.push({
            field: ValidationField.Category,
            message: 'Category is required.',
        });
    }
    if (!input.countryCode) {
        errors.push({
            field: ValidationField.Country,
            message: 'Country is required.',
        });
    }
    if (options.directFulfillment &&
        input.shippingProfileId == null) {
        errors.push({
            field: ValidationField.ShippingProfile,
            message: 'Profile is required.',
        });
    }
    if (options.directFulfillment && input.returnProfileId == null) {
        errors.push({
            field: ValidationField.ReturnProfile,
            message: 'Profile is required.',
        });
    }
    if (options.directFulfillment &&
        input.processingProfileId == null) {
        errors.push({
            field: ValidationField.ProcessingProfile,
            message: 'Profile is required.',
        });
    }
    const imageError = validateImageUuids(input.imageUuids);
    if (imageError)
        errors.push(imageError);
    const derivedCombinations = deriveCombinationsList(input.variations);
    if (derivedCombinations.length === 0) {
        const inventoryError = validateInventory(input.trackInventory, input.inventory);
        if (inventoryError)
            errors.push(inventoryError);
    }
    const variationEntries = Object.values(input.variations);
    const pricesVary = variationEntries.some((v) => v.pricesVary);
    if (!pricesVary) {
        if (input.priceCents == null ||
            !isValidPriceCents(input.priceCents, LISTING_LIMITS.maxPriceCents)) {
            errors.push({
                field: ValidationField.Price,
                message: 'Price is required.',
            });
        }
    }
    if (variationEntries.length > LISTING_LIMITS.maxVariations) {
        errors.push({
            field: ValidationField.Variations,
            message: `A listing can have at most ${LISTING_LIMITS.maxVariations} variations.`,
        });
    }
    const variationNames = variationEntries.map((v) => v.name.trim());
    variationEntries.forEach((variation, i) => {
        const siblingNames = variationNames.filter((_, j) => j !== i);
        errors.push(...validateVariationEntry({
            name: variation.name,
            options: Object.entries(variation.options).map(([id, o]) => ({
                id,
                name: o.name,
                priceCents: o.priceCents,
            })),
            defaultOption: variation.defaultOption,
        }, siblingNames));
    });
    const { hasActive, missingPriceKeys, missingInventoryKeys } = findInvalidCombinations(input.variations, input.combinations, input.priceCents, input.trackInventory);
    if (derivedCombinations.length > 0 && !hasActive) {
        errors.push({
            field: ValidationField.Combinations,
            message: 'At least one combination must be active.',
        });
    }
    else if (missingPriceKeys.length > 0) {
        errors.push({
            field: ValidationField.Combinations,
            message: 'Price is required for every combination.',
        });
    }
    else if (missingInventoryKeys.length > 0) {
        errors.push({
            field: ValidationField.Combinations,
            message: 'Inventory is required for every combination.',
        });
    }
    const variationImagesError = validateVariationImages(input.variations, input.variationImages);
    if (variationImagesError)
        errors.push(variationImagesError);
    const sections = input.fullDescr ?? [];
    if (sections.length > LISTING_LIMITS.maxDescrSections) {
        errors.push({
            field: ValidationField.DescrSections,
            message: `A listing can have at most ${LISTING_LIMITS.maxDescrSections} description sections.`,
        });
    }
    const sectionTitles = sections.map((s) => s.title.trim());
    sections.forEach((section, i) => {
        const siblingTitles = sectionTitles.filter((_, j) => j !== i);
        errors.push(...validateDescrSectionEntry(section, siblingTitles));
    });
    return errors;
};
